import { memo, useContext, useState } from "react";

import classnames from "classnames";
import { useFormikContext } from "formik";
import { isNotPresent, isPresent } from "neetocist";
import { joinHyphenCase } from "neetocommons/utils/general";
import { Copy } from "neetoicons";
import { Typography, Dropdown, Button, Label, Checkbox } from "neetoui";
import { Switch } from "neetoui/formik";
import { append, identity, without } from "ramda";
import { useTranslation } from "react-i18next";

import ScheduleRow from "./ScheduleRow";
import { addDefaultPeriod } from "./utils";

import ScheduleContext from "../ScheduleContext";
import { handleCopy, sortDays } from "../utils";

const Form = ({ day }) => {
  const [deletedPeriods, setDeletedPeriods] = useState([]);
  const [wdaysToCopy, setWdaysToCopy] = useState([]);

  const { values, setFieldValue } = useFormikContext();

  const { t } = useTranslation();
  const { firstDay } = useContext(ScheduleContext);

  const restoreDeletedPeriods = () => {
    setFieldValue(`wdays.${day}.periods`, deletedPeriods);
  };

  const uncheckCheckbox = () => {
    setFieldValue(`wdays.${day}.available`, false);
    setFieldValue(`wdays.${day}.periods`, []);
    if (values.wdays[day].periods[0]?.id) {
      setDeletedPeriods(
        values.wdays[day].periods
          .filter(item => item.startTime && item.endTime)
          .map(identity)
      );
    }
  };

  const checkCheckbox = () => {
    setFieldValue(`wdays.${day}.available`, true);
    if (deletedPeriods[0]?.startTime) {
      restoreDeletedPeriods(setFieldValue);
    } else if (isNotPresent(values.wdays[day].periods)) {
      setFieldValue(`wdays.${day}.periods`, [addDefaultPeriod(day)]);
    } else {
      setFieldValue(`wdays.${day}.periods`, [""]);
    }
  };

  const handleCheckbox = () => {
    if (values.wdays[day].available) {
      uncheckCheckbox();
    } else {
      checkCheckbox();
    }
  };

  const handleCopyChange = (e, day) =>
    setWdaysToCopy(e.target.checked ? append(day) : without([day]));

  return (
    <div className="neeto-ui-rounded-md neeto-ui-border-gray-300 neeto-ui-bg-white relative mb-3 w-full border px-4 @container last:mb-0 md:px-6">
      <div
        className="@xl:flex-no-wrap my-4 flex w-full flex-wrap items-start gap-2 @xl:flex-nowrap @xl:gap-4" // Added `@xl:flex-no-wrap` since neetoCal is using old version of tailwind
        data-testid="working-day-container"
      >
        <div
          className={classnames(
            "flex w-36 flex-shrink-0 items-center justify-start @xl:mt-1",
            { "mb-3 @xl:mb-0": isPresent(values.wdays[day]?.periods) }
          )}
        >
          <Switch
            aria-label={`wdays.${day}.available`}
            checked={values.wdays[day].available}
            className="flex-grow-0"
            data-testid={joinHyphenCase(day, "weekly-hours-checkbox")}
            id="day"
            name={`wdays.${day}.available`}
            onChange={handleCheckbox}
          />
          <Typography
            component="span"
            data-testid={joinHyphenCase(day, "day-text")}
            style="body2"
            weight={values.wdays[day].available ? "semibold" : "normal"}
            className={classnames("ms-2 capitalize", {
              "neeto-ui-text-gray-600": !values.wdays[day].available,
            })}
          >
            {t(`neetoMolecules.schedule.days.${day}`)}
          </Typography>
        </div>
        <ScheduleRow {...{ day }} />
        {values.wdays[day].available && (
          <Dropdown
            closeOnSelect
            buttonStyle="secondary"
            icon={Copy}
            position="right-start"
            buttonProps={{
              tooltipProps: {
                content: t("neetoMolecules.schedule.copyScheduleTooltip"),
                position: "top",
                hideAfter: 5000,
                touch: ["hold", 500],
              },
              className: "absolute end-4 top-3 @xl:static flex-shrink-0",
              "data-testid": joinHyphenCase(day, "copy-schedule-icon"),
            }}
            dropdownProps={{
              className:
                "neeto-ui-dropdown__popup neeto-molecules-schedule__dropdown",
            }}
            onClose={() => setWdaysToCopy([])}
          >
            <li>
              <Typography style="h5">
                {t("neetoMolecules.schedule.copySchedule")}
              </Typography>
            </li>
            {sortDays(firstDay)
              .filter(copyDay => copyDay !== day)
              .map(day => (
                <li key={day} onClick={e => e.stopPropagation()}>
                  <Label className="w-full cursor-pointer capitalize">
                    <Checkbox
                      checked={wdaysToCopy.includes(day)}
                      id={day}
                      label={t(`neetoMolecules.schedule.days.${day}`)}
                      onChange={e => handleCopyChange(e, day)}
                    />
                  </Label>
                </li>
              ))}
            <li>
              <Button
                data-testid="copy-schedule-submit-button"
                disabled={isNotPresent(wdaysToCopy)}
                label={t("neetoMolecules.common.actions.submit")}
                size="small"
                onClick={() => {
                  handleCopy({ values, day, setFieldValue, wdaysToCopy });
                  setWdaysToCopy([]);
                }}
              />
            </li>
          </Dropdown>
        )}
      </div>
    </div>
  );
};

export default memo(Form);
